import * as Yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { updateMessage } from '../utils'
import { ChangeEvent, useState } from 'react'
import { PatientMappingForm } from './PatientMappingForm'
import { selectLocations } from '../../location/locationSlice'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { selectPatientInfo, setAddDeviceCount } from '../ProrithmSlice'
import { editProrithmMapping } from '../../../services/ProrithmServices'
import { constants, messages, ProrithmSocketMessage, regex } from '../Constants'
import {
  Box,
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@mui/material'

interface MapToAmbulanceFormProps {
  mapTo: string
  mappingId: string | null
  categoryType: string | null
  selectedDeviceId: string
  handleClose: () => void
  sendMessageOnSocket: (payload: ProrithmSocketMessage) => void
}

const MapToAmbulanceForm: React.FC<MapToAmbulanceFormProps> = ({
  mappingId,
  mapTo,
  categoryType,
  selectedDeviceId,
  handleClose,
  sendMessageOnSocket,
}) => {
  const dispatch = useAppDispatch()
  const locations = useAppSelector(selectLocations)
  const patientInfo = useAppSelector(selectPatientInfo)
  const unidentifiedPatientDetails = useAppSelector(
    (state) => state.prorithmDevices.unidentifiedPatientDetails
  )
  const [mapToPatient, setMapToPatient] = useState<string>('')

  const handleMapToPatientChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMapToPatient((event.target as HTMLInputElement)?.value)
  }

  const getPatientId = () => {
    switch (mapToPatient) {
      case constants?.unidentified:
        return unidentifiedPatientDetails?.patientId
      case constants?.existing:
        return patientInfo?.muhId
      default:
        return null
    }
  }

  const formik = useFormik({
    initialValues: {
      ambulanceNumber: '',
      location: '',
      driverPhone: '',
      emtPhone: '',
    },
    validationSchema: Yup?.object({
      ambulanceNumber: Yup?.string()?.matches(
        regex?.alphaNumerics,
        messages?.ambulanceNumberInvalid
      ),
      location: Yup?.string(),
      driverPhone: Yup?.string()?.matches(
        regex?.validPhone,
        messages?.phoneInvalid
      ),
      emtPhone: Yup.string()?.matches(
        regex?.validPhone,
        messages?.phoneInvalid
      ),
    }),

    onSubmit: () => {
      handleDeviceMapping()
    },
  })

  const handleDeviceMapping = async () => {
    const payload = {
      categoryType: mapTo,
      patientId: getPatientId(),
      deviceId: selectedDeviceId,
      ambulance: {
        ambulanceVehicleNumber: formik?.values?.ambulanceNumber,
        ambulanceLocation: formik?.values?.location,
        driverPhone: formik?.values?.driverPhone,
        emtNumber: formik?.values?.emtPhone,
      },
    }
    const response = await editProrithmMapping(mappingId, payload)
    if (response?.success) {
      dispatch(setAddDeviceCount())
      toast(response?.message || messages?.deviceMappedSuccessfully, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })

      const updateMappingMessage = {
        messageType: constants?.update,
        messageContent: {
          message: updateMessage(categoryType, selectedDeviceId),
          macId: selectedDeviceId,
          category: mapTo,
        },
      }
      sendMessageOnSocket(updateMappingMessage)
      handleClose()
    }
  }

  return (
    <form onSubmit={formik?.handleSubmit}>
      <Box display='flex' flexDirection='row' flexWrap='wrap'>
        <Box minWidth={250} margin={1}>
          <TextField
            fullWidth
            type='text'
            margin='dense'
            id='ambulanceNumber'
            name='ambulanceNumber'
            label='Ambulance number'
            onBlur={formik?.handleBlur}
            onChange={formik?.handleChange}
            value={formik?.values?.ambulanceNumber}
            error={
              formik?.touched?.ambulanceNumber &&
              Boolean(formik?.errors?.ambulanceNumber)
            }
            helperText={
              formik?.touched?.ambulanceNumber &&
              formik?.errors?.ambulanceNumber
            }
          />
        </Box>
        <Box minWidth={250} marginY={2} marginX={1}>
          <FormControl
            fullWidth
            error={
              formik?.touched?.location && Boolean(formik?.errors?.location)
            }
          >
            <InputLabel id='location-label'>
              {constants?.selectLocation}
            </InputLabel>
            <Select
              name='location'
              id='location-select'
              labelId='location-label'
              onBlur={formik?.handleBlur}
              onChange={formik?.handleChange}
              value={formik?.values?.location}
              label={constants?.selectLocation}
            >
              {locations
                ?.filter((l) => l?.resource?.status === constants?.activeStatus)
                ?.map((d) => (
                  <MenuItem key={d?.resource?.id} value={d?.resource?.name}>
                    {d?.resource?.name}
                  </MenuItem>
                ))}
            </Select>
            {formik?.touched?.location && formik?.errors?.location && (
              <FormHelperText>{formik?.errors?.location}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <Box minWidth={250} margin={1}>
          <TextField
            fullWidth
            type='text'
            margin='dense'
            id='driverPhone'
            name='driverPhone'
            label='Driver phone number'
            onBlur={formik?.handleBlur}
            onChange={formik?.handleChange}
            value={formik?.values?.driverPhone}
            error={
              formik?.touched?.driverPhone &&
              Boolean(formik?.errors?.driverPhone)
            }
            helperText={
              formik?.touched?.driverPhone && formik?.errors?.driverPhone
            }
          />
        </Box>
        <Box minWidth={250} margin={1}>
          <TextField
            fullWidth
            type='text'
            id='emtPhone'
            margin='dense'
            name='emtPhone'
            label='EMT phone number'
            onBlur={formik?.handleBlur}
            onChange={formik?.handleChange}
            value={formik?.values?.emtPhone}
            error={
              formik?.touched?.emtPhone && Boolean(formik?.errors?.emtPhone)
            }
            helperText={formik?.touched?.emtPhone && formik?.errors?.emtPhone}
          />
        </Box>
      </Box>
      <PatientMappingForm
        mapTo={mapTo}
        mapToPatient={mapToPatient}
        handleMapToPatientChange={handleMapToPatientChange}
      />
      <Box marginY={2} display='flex' justifySelf='flex-end' maxWidth={200}>
        <Button onClick={() => handleClose()}>{constants?.close}</Button>
        <Button type='submit' disabled={!formik?.isValid}>
          {constants?.submit}
        </Button>
      </Box>
    </form>
  )
}

export default MapToAmbulanceForm
